import { useCallback, useEffect, useState } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, Link2, PlugZap } from "lucide-react";
import { api } from "@/lib/api";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

export const PCO_ERRORS = {
  cancelled: "You cancelled the Planning Center sign-in. Nothing was connected.",
  denied: "Planning Center didn't allow access. Make sure you're an Organization Administrator and try again.",
  expired: "The sign-in took too long or was already used. Please click Connect again.",
  token_failed: "Planning Center didn't accept the connection. Check that the callback URL and Client ID/Secret match your Planning Center app, then try again.",
  no_access: "Signed in, but that Planning Center account can't access People. Use an account with People permissions and try again.",
};

export const PcoConnect = ({ church }) => {
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState("");
  const [err, setErr] = useState("");
  const [confirm, setConfirm] = useState(false);
  const [params, setParams] = useSearchParams();
  const { pathname } = useLocation();
  const load = useCallback(() => api.get("/integrations/pco").then((r) => setS(r.data)).catch(() => setS({ connected: false })), []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const r = params.get("pco");
    if (!r) return;
    if (r === "connected") { toast.success("Connected to Planning Center", { id: "pco-connected" }); setErr(""); }
    else setErr(PCO_ERRORS[r] || "Something went wrong connecting to Planning Center.");
    setParams({}, { replace: true });
  }, [params, setParams]);

  const run = async (name, fn) => {
    setBusy(name);
    try { await fn(); } catch (e) { const d = e.response?.data?.detail; setErr(typeof d === "string" ? d : "Something went wrong. Please try again."); }
    finally { setBusy(""); }
  };
  const connect = () => run("connect", async () => {
    const { data } = await api.post("/integrations/pco/start", { origin_url: window.location.origin, return_path: pathname });
    window.location.href = data.url;
  });
  const test = () => run("test", async () => {
    const { data } = await api.post("/integrations/pco/test");
    setErr("");
    setS((p) => ({ ...p, org: data.org || p.org }));
    toast.success(`Connection works${data.org ? ` · ${data.org}` : ""}${data.people != null ? ` · ${data.people} people` : ""}`);
  });
  const disconnect = () => run("disconnect", async () => {
    setConfirm(false);
    await api.delete("/integrations/pco");
    await load();
    toast.success("Planning Center disconnected");
  });

  if (!s) return null;
  if (church?.role !== "owner" || !church?.billing?.pro) return null;
  return (
    <section className="card-soft p-7 mt-6" data-testid="pco-connect-section">
      <p className="font-display text-navy text-lg font-semibold flex items-center gap-2"><Link2 size={18} className="text-teal" /> Planning Center connection</p>
      {s.connected ? (
        <div className="bg-teal-soft rounded-xl p-5 mt-5 flex items-center gap-3" data-testid="pco-connected-banner">
          <CheckCircle2 className="text-teal shrink-0" />
          <p className="text-navy font-medium">Connected to Planning Center{s.org && <span className="font-normal"> · {s.org}</span>}</p>
        </div>
      ) : <p className="text-sm text-muted-ink mt-2">Connect {church.name}'s Planning Center so volunteers who agree are added to People automatically.</p>}
      {s.reauth_required && <p className="text-sm text-amber-800 bg-amber-50 rounded-xl p-4 mt-5" data-testid="pco-reauth-warning">Planning Center sign-in has expired. Disconnect and connect again. Your volunteers and results stay safe.</p>}
      {err && <p className="text-sm text-red-700 bg-red-50 rounded-xl p-4 mt-5" data-testid="pco-connect-error">{err}</p>}
      <div className="flex flex-wrap items-center gap-3 mt-6">
        {s.connected ? <>
          <button onClick={test} disabled={!!busy} className="btn-navy !py-2.5 flex items-center gap-2" data-testid="pco-test-btn"><PlugZap size={16} /> {busy === "test" ? "Testing..." : "Test Connection"}</button>
          <button onClick={() => setConfirm(true)} disabled={!!busy} className="text-sm text-red-700 underline underline-offset-4" data-testid="pco-disconnect-open-btn">Disconnect Planning Center</button>
        </> : (
          <button onClick={connect} disabled={!!busy} className="btn-teal !py-3 !px-6 text-base" data-testid="pco-oauth-connect-btn">{busy === "connect" ? "Opening Planning Center..." : "Connect Planning Center"}</button>
        )}
      </div>
      <AlertDialog open={confirm} onOpenChange={setConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Disconnect Planning Center?</AlertDialogTitle>
            <AlertDialogDescription>New assessment results will stop syncing to Planning Center People. People already added stay in Planning Center.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="pco-disconnect-cancel-btn">Keep connected</AlertDialogCancel>
            <AlertDialogAction onClick={disconnect} data-testid="pco-disconnect-confirm-btn">Disconnect</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  );
};
